import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { handleSignOut } from '@/utils/auth';

const TIMEOUT_DURATION = 30 * 60 * 1000; // 30 minutes
const WARNING_BEFORE = 5 * 60 * 1000; // Warn 5 minutes before timeout
const CONFIRM_GRACE = 60 * 1000; // Final 60s "Stay signed in?" confirm window

/**
 * Session Timeout Hook
 * Automatically logs out users after 30 minutes of inactivity.
 * Shows an early warning at 25 min and a final "Stay signed in?" confirmation
 * at 29 min so users can cancel the logout before it fires.
 */
export const useSessionTimeout = (isAuthenticated: boolean) => {
  const navigate = useNavigate();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const confirmTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const clearTimers = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (confirmTimeoutRef.current) {
      clearTimeout(confirmTimeoutRef.current);
      confirmTimeoutRef.current = null;
    }
  };

  const logout = async () => {
    clearTimers();
    // Show explanatory toast BEFORE redirecting so it actually renders.
    toast.error('Signed out after 30 minutes of inactivity — sign back in to continue.', {
      duration: 8000,
    });
    await handleSignOut('inactivity', { silent: true });
    navigate('/login');
  };

  const showWarning = () => {
    toast.warning('Your session will expire in 5 minutes due to inactivity', {
      duration: 10000,
    });
  };

  const showFinalConfirm = () => {
    toast('Still there? Your session is about to expire.', {
      description: 'Sign out in 60 seconds, or stay signed in.',
      duration: CONFIRM_GRACE,
      action: {
        label: 'Stay signed in',
        onClick: () => {
          // Reset everything — user is still active.
          resetTimer();
          toast.success('Session extended');
        },
      },
    });
  };

  const resetTimer = () => {
    if (!isAuthenticated) return;

    lastActivityRef.current = Date.now();
    clearTimers();

    // Early warning at T-5 minutes
    warningTimeoutRef.current = setTimeout(() => {
      showWarning();
    }, TIMEOUT_DURATION - WARNING_BEFORE);

    // Final confirm at T-60 seconds
    confirmTimeoutRef.current = setTimeout(() => {
      showFinalConfirm();
    }, TIMEOUT_DURATION - CONFIRM_GRACE);

    // Actual logout at T+0
    timeoutRef.current = setTimeout(() => {
      logout();
    }, TIMEOUT_DURATION);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers();
      return;
    }

    resetTimer();

    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    let throttleTimeout: NodeJS.Timeout | null = null;
    const throttledResetTimer = () => {
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          resetTimer();
          throttleTimeout = null;
        }, 1000);
      }
    };

    events.forEach((event) => {
      window.addEventListener(event, throttledResetTimer);
    });

    return () => {
      clearTimers();
      if (throttleTimeout) clearTimeout(throttleTimeout);
      events.forEach((event) => {
        window.removeEventListener(event, throttledResetTimer);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return {
    resetTimer,
    clearTimers,
  };
};
